import { useState, useEffect, useRef } from "react";
import { inventoryItemDtlApi, productApi, inventorySummaryApi, vendorApi, stockReceiptDtlApi, stockReceiptApi } from "../api/api";
import { emitDataMutation } from "../utils/appEvents";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination }       from "../hooks/usePagination";
import Pagination              from "../components/Pagination";
import SearchableDropdown      from "../components/SearchableDropdown";
import BarcodeLabelPrinter from "./BarcodeLabelPrinter";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";

/* ── Operational Hub Nav ── */
function HubNav({ active = "Stocks" }) {
  const navTo = (target) => {
    window.dispatchEvent(new CustomEvent("reva:navigate", { detail: target }));
  };

  return (
    <div className="rv-hub-nav">
      <button
        type="button"
        className={`rv-hub-tab ${active === "Stock Receipt" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Stock Receipt")}
      >
        <ReceiptIcon /> Vendor Inward (Stock Receipts)
      </button>
      <button
        type="button"
        className={`rv-hub-tab ${active === "Stocks" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Stocks")}
      >
        <TagIcon /> Jewelry Items & Barcodes
      </button>
      <button
        type="button"
        className={`rv-hub-tab ${active === "Bulk Stock In" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Bulk Stock In")}
      >
        <BoxIcon /> Stock Balances
      </button>
      <button
        type="button"
        className={`rv-hub-tab ${active === "Inventory Transaction" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Inventory Transaction")}
      >
        <TxnIcon /> Movement Ledger
      </button>
    </div>
  );
}

/* ══════════════════════════════════════
   TOAST
══════════════════════════════════════ */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ══════════════════════════════════════
   CONFIRM DIALOG
══════════════════════════════════════ */
function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">Delete Inventory Item?</h3>
        <p className="rv-dialog-desc">
          <strong>{name}</strong> will be permanently removed. This action cannot be undone.
        </p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   INVENTORY ITEM FORM MODAL
══════════════════════════════════════ */
function InventoryItemModal({ initial, onSave, onClose, saving, products, stocks, receiptLines = [] }) {
  const isEdit = !!initial?.inventoryItemID;
  const [form, setForm] = useState({
    stockID:       initial?.stockID       ?? 0,
    productID:     initial?.productID     ?? 0,
    receiptLineID: initial?.receiptLineID ?? 0,
    grossWeight:   initial?.grossWeight   ?? "",
    lessWeight:    initial?.lessWeight    ?? "",
    netWeight:     initial?.netWeight     ?? "",
    quantity:      initial?.quantity      ?? "",
    barcodeID:     initial?.barcodeID     ?? 0,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const g = parseFloat(form.grossWeight) || 0;
    const l = parseFloat(form.lessWeight)  || 0;
    if (form.grossWeight !== "" || form.lessWeight !== "") {
      setForm(f => ({ ...f, netWeight: parseFloat((g - l).toFixed(3)) }));
    }
  }, [form.grossWeight, form.lessWeight]);

  const validate = () => {
    const e = {};
    if (!form.productID || form.productID === 0) e.productID = "Product is required.";
    if (form.quantity === "" || isNaN(Number(form.quantity)) || Number(form.quantity) < 0)
      e.quantity = "Valid quantity is required.";
    if (form.grossWeight !== "" && isNaN(Number(form.grossWeight))) e.grossWeight = "Must be a valid number.";
    if (form.lessWeight  !== "" && isNaN(Number(form.lessWeight)))  e.lessWeight  = "Must be a valid number.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...initial, ...form });
  };

  const field = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    setErrors(v => ({ ...v, [key]: "" }));
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form rv-invitem-modal">
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><InvItemIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Inventory Item" : "New Inventory Item"}</h3>
            <p className="rv-modal-sub">{isEdit ? "Update the item details below." : "Fill in details to add a new inventory item."}</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="rv-modal-body">
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Product <span className="rv-required">*</span></label>
              <SearchableDropdown
                options={products}
                valueKey="productID"
                labelKey="productName"
                value={form.productID}
                onChange={val => field("productID", Number(val))}
                placeholder="Select product…"
                searchPlaceholder="Search products…"
                error={errors.productID}
                pageSize={10}
              />
            </div>
            <div className="rv-field">
              <label className="rv-label">Linked Stock Entry</label>
              <SearchableDropdown
                options={[
                  { stockID: 0, label: "None / Auto-link" },
                  ...stocks.map(s => ({
                    stockID: s.stockID,
                    label: `Stock #${s.stockID} — ${products.find(p => p.productID === s.productID)?.productName ?? `Product ${s.productID}`} (Qty: ${s.quantity})`
                  }))
                ]}
                valueKey="stockID"
                labelKey="label"
                value={form.stockID}
                onChange={val => field("stockID", Number(val))}
                placeholder="None / Auto-link"
                searchPlaceholder="Search stocks…"
                pageSize={10}
              />
            </div>
          </div>
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Gross Weight (g)</label>
              <input className={`rv-input ${errors.grossWeight ? "rv-input--err" : ""}`} type="number" step="0.001" min="0" placeholder="0.000" value={form.grossWeight} onChange={e => field("grossWeight", e.target.value)} />
              {errors.grossWeight && <p className="rv-field-err">{errors.grossWeight}</p>}
            </div>
            <div className="rv-field">
              <label className="rv-label">Less Weight (g)</label>
              <input className={`rv-input ${errors.lessWeight ? "rv-input--err" : ""}`} type="number" step="0.001" min="0" placeholder="0.000" value={form.lessWeight} onChange={e => field("lessWeight", e.target.value)} />
              {errors.lessWeight && <p className="rv-field-err">{errors.lessWeight}</p>}
            </div>
          </div>
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Net Weight (g)</label>
              <input className="rv-input rv-invitem-netweight" type="number" step="0.001" placeholder="Auto-calculated" value={form.netWeight} onChange={e => field("netWeight", e.target.value)} />
              <p className="rv-field-hint">Auto = Gross − Less</p>
            </div>
            <div className="rv-field">
              <label className="rv-label">Quantity <span className="rv-required">*</span></label>
              <input className={`rv-input ${errors.quantity ? "rv-input--err" : ""}`} type="number" min="0" placeholder="0" value={form.quantity} onChange={e => field("quantity", e.target.value)} />
              {errors.quantity && <p className="rv-field-err">{errors.quantity}</p>}
            </div>
          </div>
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Inward Receipt Line</label>
              {receiptLines && receiptLines.length > 0 ? (
                <SearchableDropdown
                  options={[
                    { receiptLineID: 0, label: "None / Direct Item" },
                    ...receiptLines.map(rl => {
                      const prod = products.find(p => p.productID === rl.productID);
                      const prefix = rl.receiptID ? `Receipt #${rl.receiptID} · ` : "";
                      return {
                        receiptLineID: rl.receiptLineID,
                        label: `${prefix}Line #${rl.receiptLineID} · ${prod?.productName ?? `Product #${rl.productID}`} (Qty: ${rl.quantity})`
                      };
                    })
                  ]}
                  valueKey="receiptLineID"
                  labelKey="label"
                  value={form.receiptLineID}
                  onChange={val => {
                    const id = Number(val);
                    field("receiptLineID", id);
                    if (id > 0) {
                      const line = receiptLines.find(l => l.receiptLineID === id);
                      if (line) {
                        if (line.productID && (!form.productID || form.productID === 0)) {
                          field("productID", Number(line.productID));
                        }
                        if (line.totalWeight && (!form.grossWeight || Number(form.grossWeight) === 0)) {
                          field("grossWeight", String(line.totalWeight));
                        }
                        if (line.quantity && (!form.quantity || Number(form.quantity) === 0)) {
                          field("quantity", String(line.quantity));
                        }
                      }
                    }
                  }}
                  placeholder="Link inward line…"
                  searchPlaceholder="Search line…"
                  pageSize={8}
                />
              ) : (
                <input className="rv-input" type="number" min="0" placeholder="0" value={form.receiptLineID} onChange={e => field("receiptLineID", Number(e.target.value))} />
              )}
            </div>
            <div className="rv-field">
              <label className="rv-label">Barcode ID</label>
              <input className="rv-input" type="number" min="0" placeholder="Auto (0)" value={form.barcodeID} onChange={e => field("barcodeID", Number(e.target.value))} />
            </div>
          </div>
        </div>
        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   DETAIL DRAWER
══════════════════════════════════════ */
function InventoryItemDrawer({ item, products, stocks, onEdit, onPrint, onClose }) {
  const product = products.find(p => p.productID === item.productID);
  const stock   = stocks.find(s => s.stockID === item.stockID);

  return (
    <div className="rv-dialog-backdrop" onClick={onClose}>
      <div className="rv-invitem-drawer" onClick={e => e.stopPropagation()}>
        <div className="rv-invitem-drawer-header">
          <div className="rv-invitem-drawer-avatar"><InvItemIcon /></div>
          <div className="rv-invitem-drawer-title-wrap">
            <div className="rv-invitem-drawer-name">{product?.productName ?? `Product #${item.productID}`}</div>
            <span className="rv-invitem-id-chip">Item #{item.inventoryItemID}</span>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="rv-invitem-drawer-body">
          <div className="rv-invitem-detail-grid">
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><BoxIcon /> Product</span>
              <span className="rv-vendor-detail-val">{product?.productName ?? "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><HashIcon /> Stock Entry</span>
              <span className="rv-vendor-detail-val">{item.stockID ? `#${item.stockID}` : "—"}{stock ? ` (Qty: ${stock.quantity})` : ""}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><WeightIcon /> Gross Weight</span>
              <span className="rv-vendor-detail-val">{item.grossWeight != null && item.grossWeight !== 0 ? `${item.grossWeight} g` : "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><WeightIcon /> Less Weight</span>
              <span className="rv-vendor-detail-val">{item.lessWeight != null && item.lessWeight !== 0 ? `${item.lessWeight} g` : "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><WeightIcon /> Net Weight</span>
              <span className="rv-vendor-detail-val rv-invitem-netval">{item.netWeight != null && item.netWeight !== 0 ? `${item.netWeight} g` : "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><CubeIcon /> Quantity</span>
              <span className="rv-vendor-detail-val rv-invitem-qtyval">{item.quantity ?? "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><HashIcon /> Receipt Line</span>
              {item.receiptLineID ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="rv-vendor-detail-val">Line #{item.receiptLineID}</span>
                  <button
                    type="button"
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--taupe-dark, #9e8872)",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                      textDecoration: "underline",
                      padding: 0
                    }}
                    onClick={() => {
                      onClose();
                      window.dispatchEvent(new CustomEvent("reva:navigate", { detail: "Stock Receipt" }));
                    }}
                  >
                    View Inward Receipts →
                  </button>
                </div>
              ) : (
                <span className="rv-vendor-detail-val">—</span>
              )}
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><BarcodeIcon /> Barcode ID</span>
              <span className="rv-vendor-detail-val">{item.barcodeID || "—"}</span>
            </div>
            <div className="rv-invitem-detail-item rv-invitem-detail-item--full">
              <span className="rv-vendor-detail-label"><CalIcon /> Last Updated</span>
              <span className="rv-vendor-detail-val">{item.lastUPDTTM ? new Date(item.lastUPDTTM).toLocaleString() : "—"}</span>
            </div>
          </div>
        </div>
        <div className="rv-invitem-drawer-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Close</button>
          <button className="rv-btn rv-btn--ghost" onClick={() => { onClose(); onPrint(item); }}>
            <PrintIcon /> Print Label
          </button>
          <button className="rv-btn rv-btn--primary" onClick={() => { onClose(); onEdit(item); }}>
            <EditIcon /> Edit
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════ */
export default function InventoryItemPage() {
  const [items,        setItems]        = useState([]);
  const [products,     setProducts]     = useState([]);
  const [stocks,       setStocks]       = useState([]);
  const [vendors,      setVendors]      = useState([]);
  const [receiptLines, setReceiptLines] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [filterProduct, setFilterProduct] = useState("all");

  const [modal,   setModal]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [drawer,  setDrawer]  = useState(null);
  const [toast,   setToast]   = useState(null);
  const [printItem, setPrintItem] = useState(null);

  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  useResizableColumns(tableRef, tableReady, [50, 200, 110, 90, 90, 90, 100, 120]);

  const fetchAll = async () => {
    try {
      setLoading(true); setTableReady(false);
      const [itemsData, productsData, stocksData, vendorsData, receiptLinesData, receiptsData] = await Promise.all([
        inventoryItemDtlApi.getAll(),
        productApi.getAll(),
        inventorySummaryApi.getAll(),
        vendorApi.getAll().catch(() => []),
        stockReceiptDtlApi.getAll().catch(() => []),
        stockReceiptApi.getAll().catch(() => []),
      ]);
      setItems(Array.isArray(itemsData) ? itemsData : itemsData?.data ?? []);
      setProducts(Array.isArray(productsData) ? productsData : productsData?.data ?? []);
      setStocks(Array.isArray(stocksData) ? stocksData : stocksData?.data ?? []);
      setVendors(Array.isArray(vendorsData) ? vendorsData : vendorsData?.data ?? []);

      const allReceipts = Array.isArray(receiptsData) ? receiptsData : receiptsData?.data ?? [];
      const allLines = Array.isArray(receiptLinesData) ? receiptLinesData : receiptLinesData?.data ?? [];

      // Frontend Safety Filter: Only include lines belonging to existing active Stock Receipts
      if (allReceipts.length > 0) {
        const validReceiptIds = new Set(allReceipts.map(r => Number(r.receiptID)));
        setReceiptLines(allLines.filter(line => validReceiptIds.has(Number(line.receiptID))));
      } else {
        setReceiptLines(allLines);
      }
    } catch (err) {
      showToast(err.message || "Failed to load inventory items.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  useEffect(() => {
    const pre = sessionStorage.getItem("reva_selected_stock_item");
    if (pre) {
      sessionStorage.removeItem("reva_selected_stock_item");
      setSearch(pre);
    }
    const handleFilterStock = (e) => {
      if (e?.detail) setSearch(String(e.detail));
    };
    window.addEventListener("reva:filter_stock_item", handleFilterStock);
    return () => window.removeEventListener("reva:filter_stock_item", handleFilterStock);
  }, []);

  const showToast = (message, type = "success") => setToast({ message, type });

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (formData.inventoryItemID) {
        await inventoryItemDtlApi.update(formData);
        showToast("Inventory item updated successfully.");
      } else {
        await inventoryItemDtlApi.create(formData);
        showToast("Inventory item created successfully.");
      }
      setModal(null);
      emitDataMutation("stock");
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await inventoryItemDtlApi.remove(confirm.id);
      showToast("Inventory item deleted.");
      setConfirm(null);
      emitDataMutation("stock");
      fetchAll();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  const productName = (id) =>
    products.find(p => p.productID === id)?.productName ?? `Product #${id}`;

  const usedProductIDs = [...new Set(items.map(i => i.productID))];

  const filtered = items.filter(item => {
    const pName = productName(item.productID).toLowerCase();
    const matchSearch =
      pName.includes(search.toLowerCase()) ||
      String(item.inventoryItemID).includes(search) ||
      String(item.barcodeID).includes(search);
    const matchProduct = filterProduct === "all" || item.productID === Number(filterProduct);
    return matchSearch && matchProduct;
  });

  const { paged, page, totalPages, pageSize, setPage, setPageSize, pageStart, pageEnd, totalItems } =
    usePagination(filtered, 10);

  return (
    <div className="rv-invitem-page">

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {confirm && (
        <ConfirmDialog
          name={`Item #${confirm.id}`}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}

      {modal && (
        <InventoryItemModal
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
          products={products}
          stocks={stocks}
          receiptLines={receiptLines}
        />
      )}

      {drawer && (
        <InventoryItemDrawer
          item={drawer}
          products={products}
          stocks={stocks}
          onEdit={(item) => setModal({ data: item })}
          onPrint={(item) => setPrintItem(item)}
          onClose={() => setDrawer(null)}
        />
      )}

      {/* Print Label Modal */}
      {printItem && (
        <BarcodeLabelPrinter
          item={printItem}
          product={products.find(p => p.productID === printItem.productID)}
          products={products}
          vendors={vendors}
          onClose={() => setPrintItem(null)}
        />
      )}

      <HubNav active="Stocks" />

      {/* Topbar */}
      <div className="rv-invitem-topbar">
        <div className="rv-invitem-topbar-left">
          <h2 className="rv-invitem-heading">Inventory Items</h2>
          <span className="rv-invitem-count">{items.length} total</span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
          <PlusIcon /> New Item
        </button>
      </div>

      {/* Filters */}
      <div className="rv-invitem-filters">
        <div className="rv-invitem-search">
          <SearchIcon />
          <input
            placeholder="Search by product, item ID, barcode…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-invitem-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>
        <div className="rv-invitem-product-filter">
          <SearchableDropdown
            options={[
              { id: "all", name: "All Products" },
              ...usedProductIDs.map(id => ({ id: String(id), name: productName(id) }))
            ]}
            valueKey="id"
            labelKey="name"
            value={filterProduct}
            onChange={val => { setFilterProduct(val); setPage(1); }}
            placeholder="All Products"
            searchPlaceholder="Search products…"
            pageSize={10}
          />
        </div>
      </div>

      {/* Table card */}
      <div className="rv-card rv-invitem-card">
        {/* Desktop Table View */}
        <div className="rv-desktop-table-view">
          {loading ? (
            <div className="rv-invitem-empty">
              <div className="rv-loader" />
              <p>Loading inventory items…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="rv-invitem-empty">
              <div className="rv-empty-icon"><InvItemIcon /></div>
              <p className="rv-empty-title">No inventory items found</p>
              <p className="rv-empty-sub">{search ? "Try a different search term." : "Add your first inventory item to get started."}</p>
              {!search && (
                <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                  <PlusIcon /> New Item
                </button>
              )}
            </div>
          ) : (
            <div className="rv-table-wrap rv-invitem-table-wrap">
              <table className="rv-table rv-invitem-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Gross Wt</th>
                    <th>Less Wt</th>
                    <th>Net Wt</th>
                    <th>Qty</th>
                    <th>Barcode ID</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((item, i) => (
                    <tr
                      key={item.inventoryItemID}
                      className="rv-invitem-row"
                      onClick={() => setDrawer(item)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="rv-invitem-idx">{pageStart + i}</td>
                      <td>
                        <div className="rv-invitem-product-cell">
                          <span className="rv-invitem-product-name">{productName(item.productID)}</span>
                          {item.stockID ? <span className="rv-invitem-stock-chip">{item.stockID}</span> : null}
                        </div>
                      </td>
                      <td>
                        <span className="rv-invitem-weight">
                          {item.grossWeight != null && item.grossWeight !== 0 ? `${item.grossWeight} g` : <span className="rv-empty-val">—</span>}
                        </span>
                      </td>
                      <td>
                        <span className="rv-invitem-weight">
                          {item.lessWeight != null && item.lessWeight !== 0 ? `${item.lessWeight} g` : <span className="rv-empty-val">—</span>}
                        </span>
                      </td>
                      <td>
                        <span className="rv-invitem-netweight-val">
                          {item.netWeight != null && item.netWeight !== 0 ? `${item.netWeight} g` : <span className="rv-empty-val">—</span>}
                        </span>
                      </td>
                      <td><span className="rv-invitem-qty">{item.quantity ?? 0}</span></td>
                      <td>
                        {item.barcodeID
                          ? <span className="rv-invitem-barcode">{item.barcodeID}</span>
                          : <span className="rv-empty-val">—</span>}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="rv-actions">
                          <button
                            className="rv-action-btn rv-action-btn--print"
                            title="Print Label"
                            onClick={() => setPrintItem(item)}
                          >
                            <PrintIcon />
                          </button>
                          <button
                            className="rv-action-btn rv-action-btn--edit"
                            title="Edit"
                            onClick={() => setModal({ data: item })}
                          >
                            <EditIcon />
                          </button>
                          <button
                            className="rv-action-btn rv-action-btn--del"
                            title="Delete"
                            onClick={() => setConfirm({ id: item.inventoryItemID, name: `Item #${item.inventoryItemID}` })}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mobile Cards View */}
        <div className="rv-mobile-cards-view">
          <RvDataCardList
            loading={loading}
            items={paged}
            emptyIcon={<InvItemIcon />}
            emptyTitle="No inventory items found"
            emptySubtitle={search ? "Try a different search term." : "Add your first inventory item to get started."}
            emptyAction={
              !search && (
                <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
                  <PlusIcon /> New Item
                </button>
              )
            }
            renderCard={(item, i) => (
              <RvDataCard
                key={item.inventoryItemID}
                index={pageStart + i}
                title={productName(item.productID)}
                subtitle={`Item #${item.inventoryItemID}${item.barcodeID ? ` • Barcode: ${item.barcodeID}` : ""}`}
                badge={item.stockID ? <span className="rv-invitem-stock-chip">Stock #{item.stockID}</span> : null}
                fields={[
                  { label: "Gross Wt", value: item.grossWeight != null && item.grossWeight !== 0 ? `${item.grossWeight} g` : "—" },
                  { label: "Less Wt",  value: item.lessWeight != null && item.lessWeight !== 0 ? `${item.lessWeight} g` : "—" },
                  { label: "Net Wt",   value: item.netWeight != null && item.netWeight !== 0 ? `${item.netWeight} g` : "—" },
                  { label: "Quantity", value: item.quantity ?? 0 },
                ]}
                onClick={() => setDrawer(item)}
                actions={
                  <div className="rv-actions" onClick={e => e.stopPropagation()}>
                    <button
                      className="rv-action-btn rv-action-btn--print"
                      title="Print Label"
                      onClick={() => setPrintItem(item)}
                    >
                      <PrintIcon />
                    </button>
                    <button
                      className="rv-action-btn rv-action-btn--edit"
                      title="Edit"
                      onClick={() => setModal({ data: item })}
                    >
                      <EditIcon />
                    </button>
                    <button
                      className="rv-action-btn rv-action-btn--del"
                      title="Delete"
                      onClick={() => setConfirm({ id: item.inventoryItemID, name: `Item #${item.inventoryItemID}` })}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                }
              />
            )}
          />
        </div>

        {/* Shared Pagination for Desktop & Mobile */}
        {!loading && filtered.length > 0 && (
          <Pagination
            page={page} totalPages={totalPages} pageSize={pageSize}
            setPage={setPage} setPageSize={setPageSize}
            pageStart={pageStart} pageEnd={pageEnd} totalItems={totalItems}
          />
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint rv-desktop-table-view">
          <GripIcon /> Drag column borders to resize · Click a row to view details
        </p>
      )}

      <style>{`
        .rv-action-btn--print:hover {
          background: rgba(185,167,146,.18);
          color: var(--taupe-dark, #8a7060);
        }
      `}</style>
    </div>
  );
}

/* ── Icons ── */
function PlusIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon()       { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function EditIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function TrashIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>; }
function CloseIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function CheckIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>; }
function AlertIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function GripIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{width:13,height:13}}><circle cx="9" cy="5" r="1" fill="currentColor"/><circle cx="9" cy="12" r="1" fill="currentColor"/><circle cx="9" cy="19" r="1" fill="currentColor"/><circle cx="15" cy="5" r="1" fill="currentColor"/><circle cx="15" cy="12" r="1" fill="currentColor"/><circle cx="15" cy="19" r="1" fill="currentColor"/></svg>; }
function InvItemIcon()      { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>; }
function BoxIcon()          { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>; }
function WeightIcon()       { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="8" r="4"/><path d="M6 20h12l-2-8H8L6 20z"/></svg>; }
function CubeIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3H8l-2 4h12l-2-4z"/></svg>; }
function HashIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>; }
function BarcodeIcon()      { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="1" y="4" width="2" height="16"/><rect x="5" y="4" width="1" height="16"/><rect x="8" y="4" width="2" height="16"/><rect x="12" y="4" width="1" height="16"/><rect x="15" y="4" width="2" height="16"/><rect x="19" y="4" width="1" height="16"/><rect x="21" y="4" width="2" height="16"/></svg>; }
function CalIcon()          { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>; }
function PrintIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>; }
function TagIcon()          { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="20" height="20"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>; }
function ReceiptIcon()      { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>; }
function TxnIcon()          { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M7 16V4m0 0L3 8m4-4l4 4m6 4v12m0 0l4-4m-4 4l-4-4"/></svg>; }